import * as Sentry from '@sentry/react';

/**
 * Envía un log personalizado a Sentry.
 *
 * @param {('error'|'warning'|'info')} level - Nivel del log.
 * @param {string} message - Mensaje descriptivo del log.
 * @param {object} [options] - Opciones adicionales para contextualizar el log.
 * @param {object} [options.user] - Información del usuario (ej: { id, email, username, role }).
 * @param {object} [options.tags] - Tags personalizados (ej: { section: 'checkout', action: 'submit' }).
 * @param {object} [options.extra] - Datos adicionales (ej: { cartTotal: 100, items: [...] }).
 * @param {Error} [options.error] - Error real, para capturar stacktrace (opcional).
 */
export function logToSentry(level, message, options = {}) {
  const { user, tags, extra, error } = options;
  try {
    Sentry.withScope((scope) => {
      // Añadir usuario si se proporciona
      if (user) {
        scope.setUser(user);
      }

      // Añadir tags si se proporcionan
      if (tags) {
        Object.entries(tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      // Añadir datos adicionales si se proporcionan
      if (extra) {
        Object.entries(extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      // Captura de error o mensaje
      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(message, level);
      }
    });
  } catch (error) {
    console.error('Error al enviar el log a Sentry:', error);
  }
}

/**
 * Logs a not found error to Sentry with context information
 *
 * @param {object} user - User information
 * @param {boolean} isStudent - Whether the user is a student
 * @param {object} location - React Router location object
 */
export function logNotFoundError(user, isStudent, location) {
  logToSentry('warning', 'User landed on not found page', {
    user: user
      ? {
          id: user.guid,
          username: user.username,
          email: user.email || user.username,
          role: user.role_name,
        }
      : undefined,
    tags: {
      page: 'not_found',
      isStudent: isStudent,
    },
    extra: {
      currentPath: location.pathname,
      referrer: document.referrer,
      queryParams: location.search,
      previousPath: location.state?.from,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      userSource: user?.source,
    },
  });
}

/**
 * Logs a viewer error to Sentry with context information
 *
 * @param {object} user - User information
 * @param {boolean} isStudent - Whether the user is a student
 * @param {object} location - React Router location object
 * @param {object} extra - Extra information of Viewer error
 */
export function logViewerError(user, isStudent, location, extra) {
  if (!window.location.host.includes('localhost:')) {
    logToSentry('warning', 'Viewer error', {
      user: user
        ? {
            id: user.guid,
            username: user.username,
            email: user.email || user.username,
            role: user.role_name,
          }
        : undefined,
      tags: {
        page: 'viewer',
        isStudent: isStudent,
      },
      extra: {
        ...extra,
        currentPath: location.pathname,
        referrer: document.referrer,
        queryParams: location.search,
        previousPath: location.state?.from,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        userSource: user?.source,
      },
    });
  }
}
